<script setup lang="ts">
import { ChevronRight, Briefcase, Building2, Inbox, FolderMinus, LoaderCircle, ArrowUpRight, MessagesSquare, Table2 } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { partitionAllowedFiles } from '~~/shared/allowedFileTypes'
import { hasMarcusPayload, hasNativeFiles, parseDropPayload } from '~~/shared/documentDrop'
import type { ItemRow } from '~~/shared/items/itemRow'
import { useDocumentsStore } from '~/stores/documents'
import { useDocumentsIndexingStore } from '~/stores/documentsIndexing'
import { useDroppedDocuments } from '~/composables/useDroppedDocuments'
import { useLinkDocumentToGroup } from '~/composables/useLinkDocumentToGroup'
import type { ResolvedDocumentGroup } from '~/composables/useDocumentGroups'
import BrowserItemList from '~/components/documents/BrowserItemList.vue'
import BrowserItemGrid from '~/components/documents/BrowserItemGrid.vue'
import LoadFailureNotice from '~/components/documents/LoadFailureNotice.vue'

const { t } = useI18n()
const documents = useDocumentsStore()
const { groupsLoading, orderedSources, groupsFailed } = storeToRefs(documents)

/**
 * The empty message names the ONE selected source when there is one, and stays generic otherwise —
 * with two sources selected, every per-source message would be a claim about only half the view
 * (AlexAmin/marcus#158). Zero sources never reaches here: that is the flat listing, whose own empty
 * state belongs to the surface rendering it.
 */
const emptyMessage = computed(() => {
  const only = orderedSources.value.length === 1 ? orderedSources.value[0] : null
  if (only === 'project') return t('browser.groups.emptyProject')
  if (only === 'sheet') return t('browser.groups.emptySheet')
  if (only === 'conversation') return t('browser.groups.emptyConversation')
  if (only === 'case') return t('browser.groups.emptyCase')
  return t('browser.groups.emptySources')
})

/**
 * Groups arrive already resolved, filtered and sorted — this component renders, it does not decide
 * (MAR-125). It used to read `visibleGroups` off the store while ALSO accepting a `groups` override,
 * so the sidebar and the finder disagreed about who owned filtering. Each parent now applies its own
 * filter over `useDocumentGroups()`, which is what let the store drop its display state.
 *
 * `filtered` only picks the empty message: "no results" reads wrong when nothing was searched for.
 *
 * `variant` is passed STRAIGHT THROUGH to `BrowserItemList` and nothing here reads it
 * (AlexAmin/marcus#169). The band's own markup — chevron, icon, truncating label, hover open-button,
 * count — does not change between the finder and the rail, and that is the claim Draft 2 was chosen
 * on: the page and the rail render ONE band component. The column header row is a sibling of this
 * component, above it, rather than four more controls competing with the group name inside it — see
 * `BrowserTableHeader` for why.
 */
const props = withDefaults(defineProps<{
  groups: ResolvedDocumentGroup[]
  view?: 'list' | 'grid'
  filtered?: boolean
  variant?: 'list' | 'table'
}>(), { view: 'list', filtered: false, variant: 'list' })

const emit = defineEmits<{
  contextmenu: [e: MouseEvent, item: ItemRow]
}>()

// Collapse is this list's own display state — it survives as long as the list is on screen, which is
// the whole lifetime a user would expect it to.
const collapsed = ref<Set<string>>(new Set())

function isGroupCollapsed(id: string): boolean {
  return collapsed.value.has(id)
}

function toggleGroupCollapsed(id: string) {
  const next = new Set(collapsed.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  collapsed.value = next
}

/**
 * Human label + icon per group kind (the synthetic buckets are labeled here).
 *
 * The aggregate conversations bucket (`kind: 'conversation'`, Clientes mode only) reads its label
 * from `useSyntheticGroupLabels`, which owns the tú/usted choice — the finder names the same bucket
 * from folders mode (AlexAmin/marcus#145), and two copies of that choice is how one of them ends up
 * hardcoding the informal string.
 *
 * Neither `sheet` nor `conversationThread` is one of those: since AlexAmin/marcus#150 (round 1 for
 * sheets, round 2 for threads) each is a REAL group carrying its own name, exactly like a case or a
 * client. `conversationsLabel`/`sheetsLabel` still exist, but only for the aggregate bucket and the
 * finder's signpost respectively — never for an individual thread or sheet group.
 */
const { conversationsLabel } = useSyntheticGroupLabels()

function groupLabel(group: ResolvedDocumentGroup): string {
  if (group.kind === 'none') return t('browser.groups.noClient')
  if (group.kind === 'ungrouped') return t('browser.groups.unfiled')
  if (group.kind === 'unattached') return t('browser.groups.unattached')
  if (group.kind === 'conversation') return conversationsLabel.value
  return group.name
}

function groupIcon(group: ResolvedDocumentGroup) {
  if (group.kind === 'case') return Briefcase
  if (group.kind === 'project') return Building2
  if (group.kind === 'none') return FolderMinus
  if (group.kind === 'conversation' || group.kind === 'conversationThread') return MessagesSquare
  if (group.kind === 'sheet') return Table2
  return Inbox
}

/** Destination page for real case/project/sheet/thread groups (synthetic buckets have none). */
function groupHref(group: ResolvedDocumentGroup): string | null {
  if (group.kind === 'case') return `/app/cases/${group.id}`
  if (group.kind === 'project') return `/app/clients/${group.id}`
  // A sheet group is a real sheet now, so it opens like one (AlexAmin/marcus#150).
  if (group.kind === 'sheet') return `/app/sheets/${group.id}`
  // A per-thread group opens the chat itself (AlexAmin/marcus#150 round 2) — the same route
  // `app/history.vue` pushes to for a conversation row.
  if (group.kind === 'conversationThread') return `/app/c/${group.id}`
  return null
}

/** Tooltip on the group header's open button — one string per destination kind. */
function openGroupTitle(group: ResolvedDocumentGroup): string {
  if (group.kind === 'project') return t('browser.groups.openClient')
  if (group.kind === 'sheet') return t('browser.groups.openSheet')
  if (group.kind === 'conversationThread') return t('browser.groups.openConversation')
  return t('browser.groups.openCase')
}

/**
 * Documents held back by the per-group page cap (MAR-93).
 *
 * The sidebar loads one page per group, so a large case renders a prefix. Saying so — and pointing at
 * the surface that CAN show the rest — is the whole reason the cap is acceptable: a list that stops
 * at 100 without mentioning it reads as "this case has 100 documents".
 */
function hiddenCount(group: ResolvedDocumentGroup): number {
  return Math.max(0, group.total - group.documents.length)
}

/** Where "+N more" goes: the case/client page, or the finder for the synthetic buckets. */
function moreHref(group: ResolvedDocumentGroup): string {
  return groupHref(group) ?? '/app/docs'
}

// ── Drop onto a group header: LINK into that case or client (AlexAmin/marcus#84, #83) ───────────
/**
 * A document dropped on a case header is LINKED to that case; dropped on a client header, to that
 * client. This is an entity link, not a path move — `useFolderDrop` owns the other one, and
 * conflating them is what produced #84's wrong premise that "cases appear to work" (folder-to-folder
 * moves were being read as case linking). Before #84 the headers carried no drag bindings at all, so
 * neither kind of drop did anything here.
 *
 * ── WHY THE CLIENT HALF LANDED SECOND ───────────────────────────────────────────────────────────
 *
 * The endpoint (`POST /api/clients/:id/documents`) already existed, but the browser could not show
 * the result: a client group's documents were DERIVED from its cases, and `listOrgDocuments`
 * aggregated `case_documents` alone, so the row would be written and then not appear anywhere. The
 * header was deliberately left inert rather than shipped as an affordance that silently does
 * nothing. `client_ids` on the listing row and `clientDirectIds` in the documents store are what
 * make the write visible, so the target is live now.
 */
const indexing = useDocumentsIndexingStore()
const { linkDocumentToGroup } = useLinkDocumentToGroup()
const { resolveDroppedDocuments } = useDroppedDocuments()

/** The header currently lit up as a drop target, if any. */
const dropTargetGroupId = ref<string | null>(null)

/** Real cases and real clients can be linked into; the synthetic buckets have nothing to write. */
function acceptsDrop(group: ResolvedDocumentGroup): boolean {
  return group.kind === 'case' || group.kind === 'project'
}

/** Link one document into the group — only ever called for a real case or client (`acceptsDrop`). */
async function linkInto(group: ResolvedDocumentGroup, itemId: string): Promise<void> {
  await linkDocumentToGroup(group.kind as 'case' | 'project', group.id, itemId)
}

/** Upload new files into the library, then link each into the case or client. */
async function uploadAndLink(group: ResolvedDocumentGroup, files: File[]): Promise<string | null> {
  const { valid, disallowed } = partitionAllowedFiles(files)
  // Named, never just dropped (#804) — including when it arrives mixed in with files that DO
  // upload, which used to vanish with nothing said about them.
  const rejected = disallowed.length
    ? t('documents.unsupportedTypesNamed', { names: disallowed.map(f => f.name).join(', ') })
    : null
  if (!valid.length) return rejected
  const unlinked: string[] = []
  const created = await indexing.upload(valid)
  for (const item of created) {
    if (!item?.id) continue
    try {
      await linkInto(group, item.id)
    } catch (err) {
      // Reported, never swallowed: an upload that was never linked is indistinguishable from a
      // successful one — the file is in the library, silently unfiled. Same lesson `useCaseDocuments`
      // records in as many words.
      unlinked.push(item.name ?? item.file_name ?? item.id)
      console.error('[marcus:browser] Failed to link uploaded document into group:', group.id, item.id, err)
    }
  }
  await documents.refreshAll()
  const linkErr = unlinked.length ? t('clients.errors.uploadNotLinked', { names: unlinked.join(', ') }) : null
  return [rejected, linkErr].filter(Boolean).join(' ') || null
}

function onGroupDragOver(e: DragEvent, group: ResolvedDocumentGroup) {
  if (!acceptsDrop(group)) return
  const native = hasNativeFiles(e.dataTransfer?.types)
  if (!native && !hasMarcusPayload(e.dataTransfer?.types)) return
  e.preventDefault()
  e.stopPropagation()
  // `startDrag` (documents.ts) fixes `effectAllowed = 'copyMove'` for every drag out of a row, which
  // permits ONLY `dropEffect` 'copy' or 'move' — 'link' is not a member, so the browser silently
  // vetoes the drop and never dispatches `drop` at all (AlexAmin/marcus#846). Match the sibling
  // folder-drop path (`useFolderDrop.ts`), which already uses 'move' here.
  if (e.dataTransfer) e.dataTransfer.dropEffect = native ? 'copy' : 'move'
  dropTargetGroupId.value = group.id
}

function onGroupDragLeave(e: DragEvent) {
  // Moving onto a child of the header is not a leave — without this the highlight flickers.
  const el = e.currentTarget as HTMLElement
  if (el.contains(e.relatedTarget as Node)) return
  dropTargetGroupId.value = null
}

/**
 * BOTH drop kinds are handled here (#83). Native files used to be left to bubble to a window-level
 * handler in `PanelLayout.vue`, which called back into an `onFiles` registered on dragover; that
 * handler was the global catch-all drop zone and is gone, so a surface that wants a drop now owns
 * it end to end. A group header names a real case or client, so it is exactly the kind of drop that
 * survives — the target is the thing you dropped onto.
 */
async function onGroupDrop(e: DragEvent, group: ResolvedDocumentGroup) {
  dropTargetGroupId.value = null
  if (!acceptsDrop(group)) return
  const native = hasNativeFiles(e.dataTransfer?.types)
  if (!native && !hasMarcusPayload(e.dataTransfer?.types)) return
  e.preventDefault()
  e.stopPropagation()
  if (native) {
    try {
      const err = await uploadAndLink(group, Array.from(e.dataTransfer?.files ?? []))
      if (err) alert(err)
    } catch (err: any) {
      // `indexing.upload` throws only when the WHOLE drop failed; per-file failures are already on
      // screen as their own rows. Reported here because the window handler used to do it.
      console.error('[marcus:browser] Group drop upload failed:', err)
      alert(err?.message || t('browser.groups.uploadFailed'))
    }
    return
  }
  try {
    const docs = await resolveDroppedDocuments(parseDropPayload(e.dataTransfer))
    // Skip what this group already holds: the insert is idempotent server-side, but a no-op round
    // trip per already-linked document is pure latency on a multi-document drop.
    const have = new Set(group.documents.map(d => d.id))
    for (const doc of docs) {
      if (have.has(doc.id)) continue
      await linkInto(group, doc.id)
    }
  } catch (err: any) {
    console.error('[marcus:browser] Group drop link failed:', err)
    alert(err?.message
      || t(group.kind === 'project' ? 'browser.groups.linkFailedClient' : 'browser.groups.linkFailed'))
  }
}
</script>

<template>
  <div>
    <!-- Loading (first load only; refreshes keep the current groups) -->
    <div v-if="groupsLoading && props.groups.length === 0" class="flex items-center justify-center py-12">
      <div class="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>

    <!-- No results (search or an active finder filter emptied every group) -->
    <div v-else-if="props.groups.length === 0 && props.filtered" class="px-4 py-12 text-center">
      <p class="text-sm text-muted-foreground">{{ t('common.noResults') }}</p>
    </div>

    <!-- Empty (no groups at all) -->
    <!--
      A GROUP SEED THAT BROKE, which is not an organization with no cases (AlexAmin/marcus#469).

      THE COSTLIEST OF THE SEVEN, because grouped is the rail's DEFAULT mode and every bucket is
      seeded from one request. `fetchGroups` ended in a bare `catch {}` under a `// tracked by
      useApi` comment that is false for a 500, so a failed seed left every id list empty,
      `useDocumentGroups` resolved zero groups, and the branch below stated `emptyMessage` verbatim:
      *No documents are linked to a case yet.* A firm with two hundred matters was told it had none,
      in a sentence that reads as a fact about their account rather than about one request.

      Above the empty branch and gated on having no groups, so it can never hide a good band: a
      failed background refresh over groups already on screen keeps them and says nothing here.
    -->
    <LoadFailureNotice
      v-else-if="props.groups.length === 0 && groupsFailed"
      :message="t('documents.groupsFailed')"
      :hint="t('documents.groupsFailedHint')"
      @retry="documents.fetchGroups()"
    />

    <div v-else-if="props.groups.length === 0" class="px-4 py-12 text-center">
      <p class="text-sm text-muted-foreground">
        {{ emptyMessage }}
      </p>
    </div>

    <!-- Groups -->
    <div v-else class="py-1">
      <div v-for="(group, gi) in props.groups" :key="group.id">
        <!--
          Group header. ONE hairline above each band after the first (AlexAmin/marcus#188) — the
          whole of the spine, and the only rule this component draws.

          It used to need none, because the band was a `bg-background` strip inside a `bg-card`
          panel and the tone difference did the separating. The panel is gone, so band and page are
          now the same colour and the grouping would read as one undifferentiated run without it.
          A rule per band is the cheapest structure that survives that: no fill, no box, no rule on
          the SUBGROUPS (their indent already says they are nested, and a second level of rule is
          how the stack of frames started).
        -->
        <div
          :id="`browser-group-${group.id}`"
          class="group/header w-full flex items-center gap-1.5 px-3 py-1.5 cursor-pointer hover:bg-accent-lighter transition-colors sticky top-0 bg-background z-10"
          :class="[
            gi > 0 && 'border-t border-border',
            dropTargetGroupId === group.id && 'ring-1 ring-inset ring-primary bg-accent-lighter',
          ]"
          role="button"
          :aria-expanded="!isGroupCollapsed(group.id)"
          @click="toggleGroupCollapsed(group.id)"
          @dragover="onGroupDragOver($event, group)"
          @dragleave="onGroupDragLeave"
          @drop="onGroupDrop($event, group)"
        >
          <ChevronRight
            class="h-3.5 w-3.5 text-muted-foreground transition-transform duration-150 shrink-0"
            :class="!isGroupCollapsed(group.id) && 'rotate-90'"
          />
          <component :is="groupIcon(group)" class="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span class="flex-1 min-w-0 truncate text-xs font-medium">
            {{ groupLabel(group) }}
            <span v-if="group.subtitle" class="text-muted-foreground font-normal">· {{ group.subtitle }}</span>
          </span>
          <!-- Open the case / client page (real groups only) -->
          <button
            v-if="groupHref(group)"
            type="button"
            class="h-5 w-5 flex items-center justify-center rounded-md text-muted-foreground opacity-0 group-hover/header:opacity-100 hover:bg-accent-lighter hover:text-foreground transition-all shrink-0"
            :title="openGroupTitle(group)"
            @click.stop="navigateTo(groupHref(group)!)"
          >
            <ArrowUpRight class="h-3.5 w-3.5" />
          </button>
          <!-- The TRUE size, not the loaded count — the list below may be a capped prefix. -->
          <span class="text-[10px] text-muted-foreground shrink-0">{{ group.total }}</span>
        </div>

        <!-- Group documents (grid when the finder requests it, else the list) -->
        <div v-if="!isGroupCollapsed(group.id)">
          <!--
            Matters nested inside the client (AlexAmin/marcus#83 Finder model): each is its own
            collapsible sub-header, indented, ahead of the client's own documents — folders before
            loose files, the same ordering a real Finder window uses. `group.documents` on the
            PARENT already excludes anything reachable through one of these, so nothing here can
            render twice.
          -->
          <div v-for="sub in group.subgroups" :key="sub.id">
            <div
              class="group/header w-full flex items-center gap-1.5 pl-7 pr-3 py-1.5 cursor-pointer hover:bg-accent-lighter transition-colors sticky top-0 bg-background z-10"
              :class="dropTargetGroupId === sub.id && 'ring-1 ring-inset ring-primary bg-accent-lighter'"
              role="button"
              :aria-expanded="!isGroupCollapsed(sub.id)"
              @click="toggleGroupCollapsed(sub.id)"
              @dragover="onGroupDragOver($event, sub)"
              @dragleave="onGroupDragLeave"
              @drop="onGroupDrop($event, sub)"
            >
              <ChevronRight
                class="h-3.5 w-3.5 text-muted-foreground transition-transform duration-150 shrink-0"
                :class="!isGroupCollapsed(sub.id) && 'rotate-90'"
              />
              <component :is="groupIcon(sub)" class="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span class="flex-1 min-w-0 truncate text-xs font-medium">{{ groupLabel(sub) }}</span>
              <button
                v-if="groupHref(sub)"
                type="button"
                class="h-5 w-5 flex items-center justify-center rounded-md text-muted-foreground opacity-0 group-hover/header:opacity-100 hover:bg-accent-lighter hover:text-foreground transition-all shrink-0"
                :title="t('browser.groups.openCase')"
                @click.stop="navigateTo(groupHref(sub)!)"
              >
                <ArrowUpRight class="h-3.5 w-3.5" />
              </button>
              <span class="text-[10px] text-muted-foreground shrink-0">{{ sub.total }}</span>
            </div>
            <div v-if="!isGroupCollapsed(sub.id)">
              <BrowserItemGrid
                v-if="props.view === 'grid'"
                :items="sub.documents"
                @contextmenu="(e: MouseEvent, i: ItemRow) => emit('contextmenu', e, i)"
              />
              <BrowserItemList
                v-else
                :items="sub.documents"
                :variant="props.variant"
                :group-id="sub.id"
                @contextmenu="(e: MouseEvent, i: ItemRow) => emit('contextmenu', e, i)"
              />
              <button
                v-if="hiddenCount(sub) > 0"
                type="button"
                class="flex w-full items-center gap-1.5 px-3 py-1.5 pl-12 text-[11px] text-muted-foreground transition-colors hover:bg-accent-lighter hover:text-foreground"
                @click="navigateTo(moreHref(sub))"
              >
                <ArrowUpRight class="h-3 w-3 shrink-0" />
                {{ t('browser.groups.andMore', { n: hiddenCount(sub) }) }}
              </button>
            </div>
          </div>

          <BrowserItemGrid
            v-if="props.view === 'grid'"
            :items="group.documents"
            @contextmenu="(e: MouseEvent, i: ItemRow) => emit('contextmenu', e, i)"
          />
          <BrowserItemList
            v-else
            :items="group.documents"
            :variant="props.variant"
            :group-id="group.id"
            @contextmenu="(e: MouseEvent, i: ItemRow) => emit('contextmenu', e, i)"
          />

          <!-- Truncation, stated. Never let a capped list pass for a complete one. -->
          <button
            v-if="hiddenCount(group) > 0"
            type="button"
            class="flex w-full items-center gap-1.5 px-3 py-1.5 pl-8 text-[11px] text-muted-foreground transition-colors hover:bg-accent-lighter hover:text-foreground"
            @click="navigateTo(moreHref(group))"
          >
            <ArrowUpRight class="h-3 w-3 shrink-0" />
            {{ t('browser.groups.andMore', { n: hiddenCount(group) }) }}
          </button>
        </div>
      </div>
    </div>

    <!-- Background refresh indicator -->
    <div v-if="groupsLoading && props.groups.length > 0" class="flex items-center gap-1.5 px-3 py-1.5 text-[10px] text-muted-foreground">
      <LoaderCircle class="h-3 w-3 animate-spin" />
      {{ t('common.loading') }}
    </div>
  </div>
</template>
